import { createFeatureSelector, createSelector } from '@ngrx/store';

import { AppState, RouterStateUrl, selectRouter } from '../../NGRX/core.state';
import { ConfigurationState } from '../configuration/configuration.models';
import {FantasyCodes, MicrositePoolsState, PoolScope, PoolsMenuScope, RacingCodes, SportsCodes} from './pool.models';
import {uniq, map, isEmpty} from 'lodash-es';
import { RouterReducerState } from '@ngrx/router-store';
import { PoolList } from '../../models/pool-list';
import { selectConfigurationState } from '../configuration/configuration.selectors';
import { PoolDisplayGroup } from '../../models/pool-display-group';

import { enrichPoolData } from './combine-data.utils';
import {selectSportEventList, selectSportEventLoaded} from "../sport-events/sport-events.selector";


export const selectMicrositePoolsState = createFeatureSelector<
  AppState,
  MicrositePoolsState
>('micrositePools');

export const selectPools = createSelector(
  selectMicrositePoolsState,
  (state: MicrositePoolsState) => state
);


/**
 * Construct pools menu from entity state
 *
 * This selector builds the legacy menu structure from the entity adapter,
 * providing backward compatibility while using entity storage internally.
 */
export const selectPoolsMenu = createSelector(
  selectMicrositePoolsState,
  (state: MicrositePoolsState) => {
    if (!state.menu) return undefined;
    if (!state.poolMenuEntities) return undefined;
    
    // Convert entity state to pools dictionary
    const poolsDict: Record<string, PoolsMenuScope.Pool> = {};
    const entities = state.poolMenuEntities.entities;
    const ids = state.poolMenuEntities.ids;
    
    ids.forEach(id => {
      const pool = entities[id];
      if (pool) {
        poolsDict[String(id)] = pool;
      }
    });
    
    // Return menu structure with entity-sourced pools
    return {
      ...state.menu,
      pools: poolsDict
    };
  }
);

export const selectLeftMenu = createSelector(
  selectMicrositePoolsState,
  (state: MicrositePoolsState) => state.parsedLeftMenu
);

export const selectMenuLoaded = createSelector(
  selectMicrositePoolsState,
  (state: MicrositePoolsState) => state.menuLoaded
);

export const selectCurrentPool = createSelector(
  selectMicrositePoolsState,
  (state: MicrositePoolsState) => {
    return state.currentPool
  }
);

export const selectCurrentPoolLoading = createSelector(
  selectMicrositePoolsState,
  (state: MicrositePoolsState) => state.currentPoolLoading
);

export const selectCurrentPoolLoaded = createSelector(
  selectMicrositePoolsState,
  (state: MicrositePoolsState) => state.currentPoolLoaded
);

export const selectFeaturedPool = createSelector(
  selectMicrositePoolsState,
  (state: MicrositePoolsState) => state.featuredPool
)

export const selectHeadlinePool = createSelector(
  selectMicrositePoolsState,
  (state: MicrositePoolsState) => state.headlinePools
)

export const selectCurrentPoolSelections = createSelector(
  selectMicrositePoolsState,
  (state: MicrositePoolsState) => (state.currentPoolSelections && state.currentPool?.id) ? state.currentPoolSelections[state.currentPool.id] : undefined
);

export const selectPoolFilters = createSelector(
  selectMicrositePoolsState,
  (state: MicrositePoolsState) => state.filters
);

export const selectPoolExtraModel = createSelector(
  selectMicrositePoolsState,
  (state: MicrositePoolsState) => state.poolsExtraModel
);


export const selectPoolListMenuByView = createSelector(
  selectMicrositePoolsState,
  selectConfigurationState,
  (pools: MicrositePoolsState, configuration: ConfigurationState): Array<SportsCodes | RacingCodes | FantasyCodes | string | null> | undefined => {
    if(isEmpty(pools.menu)) return;
    const displayGroups: PoolDisplayGroup[] = pools.menu?.categories?.[String(configuration.displayState.view)]?.display_groups ?? [];
    const sportCodeList: Array<SportsCodes | RacingCodes | FantasyCodes | string | null> = uniq(map(displayGroups
      .filter(displayGroup => displayGroup.pools.length > 0),
      (dp) => dp.sport_code)
    );
    return sportCodeList;
  }
);

export const selectSportCode =
createSelector(
  selectMicrositePoolsState,
  selectConfigurationState,
  selectRouter,
  (pools: MicrositePoolsState, configuration: ConfigurationState, router: RouterReducerState<RouterStateUrl>): PoolsMenuScope.SportCode => {
    const sportCode: PoolsMenuScope.SportCode = {};
    sportCode[String(configuration.displayState.view)] = router.state.params.sport_code ? router.state.params.sport_code.replace('-', '_').toUpperCase() : pools.currentSportCode?.[String(configuration.displayState.view)];
    return sportCode;
  }
);

export const getPoolsMenuByView =
createSelector(
  selectMicrositePoolsState,
  selectConfigurationState,
  selectRouter,
  (pools: MicrositePoolsState, configuration: ConfigurationState, router: RouterReducerState<RouterStateUrl>): PoolList | undefined => {

    if (!configuration.displayState.view || !pools.menu) {
      return;
    }

    const view = configuration.displayState.view;
    const menuView = pools.menu?.categories?.[view];
    
    if (!menuView) {
      return;
    }

    let sportCode = {};
    try {
      sportCode = router.state.params.sport_code?.replace(/-/g, '_').toUpperCase() || pools?.currentSportCode?.[view];
    } catch (e: any) {
      console.error('Error getting sport code: ', e);
    }

    const subCode = router.state.params.sub_code?.replace(/-/g, '_').toUpperCase() || null;

    // Use structural sharing instead of cloneDeep - only copy what we modify
    let groups_availables: any[] = [];
    
    try {
      if (menuView.display_groups) {
        groups_availables = menuView.display_groups
          .filter(el => el.sport_code === sportCode)
          .filter(el => !subCode || el.pool_type === subCode);
      }
    } catch {
      groups_availables = [];
    }

    // Return new object with structural sharing (memoization handles caching)
    return {
      ...menuView,
      groups_availables
    };
  }
);


// Sports and fantasy stats selectors

export const getSportsStats = createSelector(
  selectMicrositePoolsState,
  (state: MicrositePoolsState) => state.sportsStats
);

export const getFantasyStats = createSelector(
  selectMicrositePoolsState,
  (state: MicrositePoolsState) => state.fantasyStats
);

export const getSportsStatsLoading = createSelector(
  selectMicrositePoolsState,
  (state: MicrositePoolsState) => state.sportsStatsLoading
);

export const getFantasyStatsLoading = createSelector(
  selectMicrositePoolsState,
  (state: MicrositePoolsState) => state.fantasyStatsLoading
);

// Combined pool selector
let lastValidEnrichedPool: PoolScope.CurrentPool | undefined = undefined;
let lastEnrichedPoolId: number | undefined = undefined;

export const selectEnrichedPool = createSelector(
  selectCurrentPool,
  selectSportEventList,
  (pool, sportEvents) => {
    // --- STEP 1: Check Primary Pool ---
    if (!pool) {
      return undefined;
    }

    const requiresEnrichment = pool.type_code === 'FANTASY_MVP' || pool.type_code === 'MVP_FANTASY';

    // --- STEP 2: Handle Non-Enriched Pool Types ---
    if (!requiresEnrichment) {
      return pool;
    }

    // --- STEP 3: Handle Race Condition ---
    if (!sportEvents || Object.keys(sportEvents).length === 0) {
      // Return cached version only if it's the SAME pool
      if (lastEnrichedPoolId === pool.id && lastValidEnrichedPool) {
        console.warn('Sport events not ready, using cached enriched pool');
        return lastValidEnrichedPool;
      }
      return undefined;
    }

    // --- STEP 4: Enrichment Execution ---
    try {
      const enriched = enrichPoolData(pool, sportEvents);

      // Validate enrichment succeeded with actual data
      if (enriched && isValidEnrichment(enriched)) {
        // const isNewPool = lastEnrichedPoolId !== pool.id;
        // const isUpdate = lastEnrichedPoolId === pool.id;

        lastValidEnrichedPool = enriched;
        lastEnrichedPoolId = enriched.id;

        // Log successful enrichment
        // if (isNewPool) {
        //   console.log('✅ Pool enriched successfully (new pool):', {
        //     poolId: enriched.id,
        //     poolName: enriched.name,
        //     sportEventsCount: Object.keys(sportEvents).length
        //   });
        // } else if (isUpdate) {
        //   console.log('🔄 Pool enriched successfully (updated):', {
        //     poolId: enriched.id,
        //     poolName: enriched.name,
        //     sportEventsCount: Object.keys(sportEvents).length
        //   });
        // }

        return enriched;
      } else {
        console.error('Enrichment produced invalid result');
        // Return cached if same pool, otherwise undefined
        return lastEnrichedPoolId === pool.id ? lastValidEnrichedPool : undefined;
      }
    } catch (error) {
      console.error('Enrichment failed with error:', error);
      // Return cached version if it's the same pool
      return lastEnrichedPoolId === pool.id ? lastValidEnrichedPool : undefined;
    }
  }
);

/**
 * Validates that enrichment actually added the required data
 * Prevents returning pools with missing/empty enriched fields
 */
function isValidEnrichment(pool: PoolScope.CurrentPool): boolean {
  if (!pool?.legs || pool.legs.length === 0) {
    return true; // Pool with no legs is technically valid
  }

  // Check that at least some selections have been properly enriched
  for (const leg of pool.legs) {
    if (!leg.selections || leg.selections.length === 0) {
      continue;
    }

    for (const selection of leg.selections) {
      const player = selection.player;

      // If selection has a sport_event_id, it MUST have enriched data
      if (player?.sport_event_id) {
        // Validate sport_event exists and is not an empty object
        if (!selection.sport_event || !isValidSportEvent(selection.sport_event)) {
          console.warn('Missing or invalid sport_event for selection:', player.sport_event_id);
          return false;
        }

        // Validate home_team exists and is not an empty object
        if (!selection.home_team || !isValidSportEventCompetitor(selection.home_team)) {
          console.warn('Missing or invalid home_team for selection:', player.sport_event_id);
          return false;
        }

        // Validate away_team exists and is not an empty object
        if (!selection.away_team || !isValidSportEventCompetitor(selection.away_team)) {
          console.warn('Missing or invalid away_team for selection:', player.sport_event_id);
          return false;
        }

        // If we found at least one properly enriched selection, it's valid
        return true;
      }
    }
  }

  // No selections required enrichment, so it's valid
  return true;
}

/**
 * Validates that a sport event object matches the expected SportEvent structure
 */
function isValidSportEvent(sportEvent: any): sportEvent is PoolScope.SportEvent {
  return (
    typeof sportEvent === 'object' &&
    typeof sportEvent.id === 'number' &&
    typeof sportEvent.name === 'string' &&
    typeof sportEvent.status === 'string' &&
    typeof sportEvent.sport_sub_code === 'string' &&
    typeof sportEvent.sport_code === 'string' &&
    Array.isArray(sportEvent.sport_event_competitors) &&
    sportEvent.sport_event_competitors.length > 0
  );
}

/**
 * Validates that a competitor object matches the expected SportEventCompetitor structure
 */
function isValidSportEventCompetitor(competitor: any): competitor is PoolScope.SportEventCompetitor {
  return (
    typeof competitor === 'object' &&
    typeof competitor.id === 'number' &&
    typeof competitor.competitor_id === 'number' &&
    typeof competitor.name === 'string' &&
    typeof competitor.status === 'string' &&
    (competitor.status === 'Home' || competitor.status === 'Away')
  );
}

export const selectCombinedDataReady = createSelector(
  selectCurrentPoolLoaded,
  selectSportEventLoaded, // Imported from sport-event.selectors
  (poolLoaded, eventsLoaded) => poolLoaded && eventsLoaded
);
